/**
 * Public menu page — the QR-code destination. Mobile-first, server-rendered so
 * dishes and prices are crawlable, and usable as the whole public page for
 * menu-only listings.
 */
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { SmartImage } from "@/components/ui/SmartImage";
import { PlaceMenuSection } from "@/components/place/PlaceMenuSection";
import { MapPin, ArrowLeft, Phone, Globe } from "lucide-react";
import {
  applyDishTranslations,
  buildMenuJsonLd,
  menuDishFromRow,
  placeMenuFromRow,
  type MenuDish,
  type PlaceMenu,
} from "@/lib/place-menu";
import { docsToPlaceMenu, loadPlaceMenuDocs, type PlaceMenuDoc } from "@/lib/place-menus";
import { HoursStatus } from "@/components/menus/HoursStatus";
import type { HoursSource } from "@/lib/opening-hours";
import { fetchManyEntityTranslations } from "@/lib/i18n-content";
import { useLanguage } from "@/i18n/LanguageContext";

type MenuPageData = {
  id: string;
  slug: string;
  name: string;
  city: string | null;
  address: string | null;
  image: string | null;
  phone: string | null;
  link: string | null;
  listingType: string;
  menu: PlaceMenu;
  menus: PlaceMenuDoc[];
  dishes: MenuDish[];
  /** Same dishes with stored Finnish text applied (used when the site is in Finnish). */
  dishesFi: MenuDish[];
  hoursSource: HoursSource;
};

export const Route = createFileRoute("/menu/$slug")({
  loader: async ({ params }): Promise<MenuPageData> => {
    const { data } = await supabase
      .from("places")
      .select("*")
      .eq("slug", params.slug)
      .eq("published", true)
      .maybeSingle();
    if (!data) throw notFound();

    const { data: rows } = await supabase
      .from("place_menu_items")
      .select("*")
      .eq("place_id", (data as any).id)
      .eq("is_active", true)
      .order("sort_order", { ascending: true });

    const menus = await loadPlaceMenuDocs(supabase, data as any);
    const dishes = ((rows ?? []) as any[]).map((r) => menuDishFromRow(r));
    const fiMap = await fetchManyEntityTranslations("menu_item", dishes.map((d) => d.id), "fi");

    return {
      id: (data as any).id,
      slug: (data as any).slug,
      name: (data as any).name,
      city: (data as any).city_slug ?? null,
      address: (data as any).address ?? null,
      image: (data as any).image_url ?? null,
      phone: (data as any).phone ?? null,
      link: (data as any).link ?? null,
      listingType: (data as any).listing_type ?? "full",
      menus,
      menu: menus.length ? docsToPlaceMenu(menus) : placeMenuFromRow(data as any),
      dishes,
      dishesFi: applyDishTranslations(dishes, fiMap),
      hoursSource: {
        opening_hours: (data as any).opening_hours ?? null,
        special_hours: (data as any).special_hours ?? null,
        temporarily_closed: (data as any).temporarily_closed ?? null,
        temporarily_closed_note: (data as any).temporarily_closed_note ?? null,
        schedule_info: (data as any).schedule_info ?? null,
      },
    };
  },
  head: ({ loaderData }) => {
    const name = loaderData?.name ?? "Menu";
    const title = `${name} menu | What Finland`;
    const description = `See the current menu, dishes and prices at ${name}${loaderData?.city ? ` in ${loaderData.city}` : ""}.`;
    const image = loaderData?.image && /^https?:\/\//.test(loaderData.image) ? loaderData.image : null;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "restaurant.menu" },
        { name: "twitter:card", content: "summary_large_image" },
        ...(image
          ? [
              { property: "og:image", content: image },
              { name: "twitter:image", content: image },
            ]
          : []),
      ],
    };
  },
  component: MenuPage,
  notFoundComponent: () => (
    <div className="mx-auto max-w-lg px-4 py-24 text-center">
      <h1 className="font-heading text-navy text-2xl">Menu not found</h1>
      <p className="mt-2 text-sm text-navy/70">This menu is not available.</p>
      <Link to="/eat" className="mt-6 inline-block rounded-full bg-navy px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-white">
        Browse restaurants
      </Link>
    </div>
  ),
  errorComponent: () => (
    <div className="mx-auto max-w-lg px-4 py-24 text-center">
      <h1 className="font-heading text-navy text-2xl">Menu unavailable</h1>
      <p className="mt-2 text-sm text-navy/70">Please try again in a moment.</p>
    </div>
  ),
});

function MenuPage() {
  const d = Route.useLoaderData();
  const { lang } = useLanguage();
  const isFi = lang === "fi";
  const dishes = isFi ? d.dishesFi : d.dishes;
  const jsonLd = buildMenuJsonLd({
    menu: d.menu,
    placeName: d.name,
    placeUrl: `https://whatfinland.com/menu/${d.slug}`,
    dishes,
  });

  return (
    <main className="min-h-screen bg-cream">
      {jsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Restaurant",
              "@id": `https://whatfinland.com/menu/${d.slug}#restaurant`,
              name: d.name,
              url: `https://whatfinland.com/menu/${d.slug}`,
              mainEntityOfPage: `https://whatfinland.com/menu/${d.slug}`,
              ...(d.image ? { image: d.image } : {}),
              ...(d.address
                ? {
                    address: {
                      "@type": "PostalAddress",
                      streetAddress: d.address,
                      ...(d.city ? { addressLocality: d.city } : {}),
                      addressCountry: "FI",
                    },
                  }
                : {}),
              ...(d.phone ? { telephone: d.phone } : {}),
              ...(d.link ? { sameAs: [d.link] } : {}),
              servesCuisine: "Restaurant",
              hasMenu: jsonLd,
            }),
          }}
        />
      )}

      <div className="relative h-52 sm:h-72 w-full overflow-hidden bg-navy">
        {d.image && (
          <SmartImage src={d.image} alt={d.name} className="w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/55 to-navy/10" />
        <div className="absolute inset-x-0 bottom-0">
          <div className="mx-auto max-w-3xl px-4 pb-8 sm:pb-10">
            <p style={{ textTransform: "none" }} className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/70">
              Menu
            </p>
            <h1 className="mt-1.5 font-heading text-white text-3xl sm:text-4xl leading-tight">{d.name}</h1>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-4 pb-20 -mt-6 relative">
        <div className="rounded-3xl border border-navy/10 bg-white p-5 sm:p-7 shadow-[0_18px_50px_-30px_rgba(16,32,64,0.55)]">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <HoursStatus source={d.hoursSource} />
          </div>
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-sm text-navy/70">
            {d.address && (
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="size-4 text-teal" aria-hidden="true" /> {d.address}
              </span>
            )}
            {d.phone && (
              <a href={`tel:${d.phone}`} className="inline-flex items-center gap-1.5 hover:text-navy">
                <Phone className="size-4 text-teal" aria-hidden="true" /> {d.phone}
              </a>
            )}
            {d.link && (
              <a href={d.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 hover:text-navy">
                <Globe className="size-4 text-teal" aria-hidden="true" /> Website
              </a>
            )}
          </div>

          <div className="mt-5 h-px w-full bg-navy/10" />

          <PlaceMenuSection menu={d.menu} menus={d.menus} dishes={dishes} placeName={d.name} lang={isFi ? "fi" : "en"} />

          {d.listingType !== "menu_only" && (
            <Link
              to="/place/$id"
              params={{ id: d.slug }}
              className="mt-8 inline-flex items-center gap-2 rounded-full border border-navy/15 px-4 py-2 text-xs font-bold uppercase tracking-widest text-navy transition hover:bg-navy/5"
            >
              <ArrowLeft className="size-4" /> Full listing
            </Link>
          )}
        </div>
      </div>

    </main>
  );
}
